import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Banner shown at top of dashboard when any debtor has an urgent deadline
 * (judgment expires ≤ 30 days OR any critical action deadline ≤ 30 days).
 *
 * Props:
 *   urgent: {
 *     debtor_id: string,
 *     name: string,
 *     days_until: number,
 *     reason: 'judgment_expires' | 'renewal_deadline' | 'other',
 *     total_urgent: number   // total urgent debtors (for +N more chip)
 *   } | null
 */
const UrgentBanner = ({ urgent, className }) => {
  const nav = useNavigate();
  if (!urgent) return null;
  const others = Math.max(0, (urgent.total_urgent || 1) - 1);
  return (
    <div
      className={cn(
        "flex items-center gap-4 px-6 py-4 rounded-[12px] border-l-4 border-danger",
        "shadow-card",
        className
      )}
      style={{
        background: "linear-gradient(90deg, #2A1A0A 0%, #1A0A0A 100%)",
      }}
      data-testid="urgent-banner"
    >
      <AlertTriangle size={24} className="text-danger shrink-0 animate-pulse-alert" />
      <div className="flex-1 text-white text-[14px] font-medium">
        <span>Urgent — </span>
        <span className="text-accent-pink font-bold" data-testid="urgent-banner-name">{urgent.name}</span>
        <span>'s judgment expires in </span>
        <span className="jea-mono text-accent-pink font-bold" data-testid="urgent-banner-days">
          {urgent.days_until}
        </span>
        <span> days. Renew now.</span>
        {others > 0 && (
          <span
            className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full bg-danger/20 text-danger text-[11px] font-bold tracking-wider"
            data-testid="urgent-banner-more"
          >
            +{others} MORE
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => nav(`/debtors/${urgent.debtor_id}`)}
        className="shrink-0 h-11 px-6 rounded-[8px] bg-accent-pink text-[#091424] font-bold text-[14px] hover:bg-accent-pink-light transition-colors"
        data-testid="urgent-banner-cta"
      >
        Take Action
      </button>
    </div>
  );
};

export default UrgentBanner;
